import React from "react";
import numeral from "numeral";
import Select from "react-select";
import { PieChart, Pie, Cell } from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faCompressArrowsAlt, faExpandArrowsAlt, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { Button, ButtonGroup } from "aible";
import { useDispatch, useSelector } from "types/store";
import { isEnabled } from "utils/featureFlags";
import { getIsTableauMode } from "containers/App/reducer";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import SmallDateChart from "components/Tiles/Sense/SmallDateChart";
import RadarIcon from "images/SenseIcons/radar-light 1.svg";
import styles from "./style.module.css";
import Tooltip from "components/Inputs/Tooltip";
import { calculateDateValues } from "components/Tiles/Sense/ActionsPanel";
import { getDataset } from "containers/Home/reducer";
import { selectOnChangeWrapper } from "utils/select";
import { getDateAnalysis, getIsOpenDataEvaluated, getIsOpenTransformations } from "containers/Sense/dataset-selectors";
import { updateTileState } from "containers/Sense/reducer";
const DataCollection = ({ dataset }) => {
    const dispatch = useDispatch();
    const dateAnalysisState = useSelector((state) => getDateAnalysis(state, dataset?.id));
    const updateDateAnalysisState = (dateAnalysis) => {
        dispatch(updateTileState(dataset?.id, { dateAnalysis }));
    };
    const isTableauMode = useSelector((state) => getIsTableauMode(state));
    const { dateFieldOptions, dateField, currentStart, currentEnd, referenceStart, referenceEnd, dateRowsExcluded, dateRowsBefore, dateFieldMax, dateRowsAfter, dateAnalysisTarget } = calculateDateValues(dataset, dateAnalysisState);
    const explorationAndAIString = ((t) => {
        switch (t) {
            case "CREATE_AI":
                return "AI creation";
        }
    })(dateAnalysisTarget);
    const pieData = [
        {
            name: "rows ignored",
            value: dateRowsExcluded,
            color: "#C8CAD4"
        },
        {
            name: " training rows",
            value: dateRowsBefore,
            color: "#8884d8"
        },
        {
            name: " comparison rows",
            value: dateRowsAfter,
            color: "#34D399"
        }
    ];
    const isTestTrainSplit = isEnabled("test-train-split") && isEnabled("aible-optimize") && !isTableauMode;
    const datasetDateField = dataset?.options?.timestamp_column;
    if (!isEnabled("show-original-whats-changed-button-flow")) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement("div", null,
        React.createElement("div", null, dataset?.outcome !== undefined &&
            isTestTrainSplit &&
            (dateFieldOptions.length > 0 ? (React.createElement("div", null,
                React.createElement("div", { className: styles.dateExploreSelect },
                    React.createElement("div", { className: styles.dateLabel }, "Would you like to use specific dates for a train test split in your prediction models?"),
                    React.createElement(ButtonGroup, null,
                        React.createElement(Button, { active: dateAnalysisTarget === undefined, onClick: () => {
                                updateDateAnalysisState({
                                    ...dateAnalysisState,
                                    dateAnalysisTarget: undefined
                                });
                            } }, "No"),
                        isTestTrainSplit && (React.createElement(Button, { active: dateAnalysisTarget === "CREATE_AI", onClick: () => {
                                updateDateAnalysisState({
                                    ...dateAnalysisState,
                                    dateAnalysisTarget: "CREATE_AI"
                                });
                            } }, "AI Training and Evaluation Split")))),
                dateAnalysisTarget === undefined ? (React.createElement(React.Fragment, null)) : (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: styles.dateTitle },
                        React.createElement("div", { className: styles.dateLabel }, "Which date field should be used for " + explorationAndAIString),
                        React.createElement("div", { className: styles.dateColSelect },
                            React.createElement(Select, { options: dateFieldOptions, onChange: selectOnChangeWrapper((val) => {
                                    return updateDateAnalysisState({
                                        ...dateAnalysisState,
                                        selectedDateField: val
                                    });
                                }), menuPlacement: "top", value: dateField === undefined ? dateField : { label: dateField?.label, value: dateField }, placeholder: "Select a Date Field" }))),
                    dateField !== undefined ? (dateField?.label !== datasetDateField && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: styles.smallDateChart },
                            React.createElement(SmallDateChart, { datasetId: dataset?.id, isSmall: true, field: dateField, comparisonOptions: dateAnalysisState, updateComparisonOptions: updateDateAnalysisState })),
                        React.createElement("div", { className: styles.dateExtraWrapper },
                            React.createElement("div", { className: styles.dateExtra },
                                dateRowsExcluded !== undefined && (React.createElement("div", { className: styles.dateInfo },
                                    React.createElement("div", { className: styles.dateDot, style: { background: "#C8CAD4" } }),
                                    React.createElement("div", { className: styles.dateMessage },
                                        React.createElement("span", { style: { fontWeight: "bold" } }, dateRowsExcluded),
                                        React.createElement("span", { style: { marginLeft: "5px" } }, "rows ignored (including missing rows which cannot be selected)")),
                                    React.createElement(Tooltip, { overlay: React.createElement("div", { className: styles.dateFieldTooltip }, dateRowsExcluded > 0
                                            ? `Your ${explorationAndAIString} will exclude data not in the reference or current periods`
                                            : `Your ${explorationAndAIString} will not exclude any rows`) }))),
                                dateRowsAfter !== undefined && (React.createElement("div", null,
                                    React.createElement("div", { className: styles.dateInfo },
                                        React.createElement("div", { className: styles.dateDot, style: { background: "#8884d8" } }),
                                        React.createElement("div", { className: styles.dateMessage },
                                            React.createElement("span", { style: { fontWeight: "bold" } }, dateRowsBefore),
                                            React.createElement("span", { style: { marginLeft: "5px" } }, dateRowsBefore
                                                ? `rows from ${referenceStart} and ${referenceEnd} ` +
                                                    `will be used for ${dateAnalysisTarget === "CREATE_AI" || dateAnalysisTarget === "BOTH"
                                                        ? "AI training"
                                                        : ""}`
                                                : "training rows")),
                                        React.createElement(Tooltip, { overlay: React.createElement("div", { className: styles.dateFieldTooltip }, dateRowsBefore === dateFieldMax
                                                ? `Your ${explorationAndAIString} will be created using all ${dateRowsBefore} rows`
                                                : `Your ${explorationAndAIString} will be created using the ${dateRowsBefore} rows ` +
                                                    `from ${referenceStart} to ${referenceEnd}` +
                                                    (dateRowsAfter
                                                        ? `and will compare them to the ${dateRowsAfter} rows ` +
                                                            `from ${currentStart} to ${currentEnd}`
                                                        : "")) })),
                                    React.createElement("div", { className: styles.dateInfo },
                                        React.createElement("div", { className: styles.dateDot, style: { background: "#34D399" } }),
                                        React.createElement("div", { className: styles.dateMessage },
                                            React.createElement("span", { style: { fontWeight: "bold" } }, dateRowsAfter),
                                            React.createElement("span", { style: { marginLeft: "5px" } }, `rows from ${currentStart} and ${currentEnd} ` +
                                                `will be used for ${dateAnalysisTarget === "CREATE_AI" || dateAnalysisTarget === "BOTH"
                                                    ? "AI evaluation"
                                                    : ""}`)),
                                        React.createElement(Tooltip, { overlay: React.createElement("div", { className: styles.dateFieldTooltip }, dateAnalysisTarget === "CREATE_AI" && (React.createElement("div", null,
                                                React.createElement("strong", null, "If you are creating a predictive model, "),
                                                React.createElement("span", null, "these rows are the data the AI will use to test the models.")))) }))))),
                            React.createElement("div", { className: styles.datePieChart },
                                React.createElement(PieChart, { width: 100, height: 100 },
                                    React.createElement(Pie, { data: pieData, labelLine: false, dataKey: "value", paddingAngle: 0 }, pieData.map((entry, index) => (React.createElement(Cell, { key: `cell-${index}`, fill: entry.color, stroke: entry.color })))))))))) : (React.createElement("div", { className: styles.dFlex, style: { marginTop: "10px", fontSize: "13px" } }, "Select a date field to begin")))))) : (React.createElement("div", null,
                React.createElement("div", { className: styles.dateLabel, style: { marginTop: "10px" } }, "No Date Field Detected")))))));
};
const DataEvaluatedPanel = ({ datasetId }) => {
    const dispatch = useDispatch();
    const dataset = useSelector((state) => getDataset(state, datasetId));
    const isOpen = useSelector((state) => getIsOpenDataEvaluated(state, datasetId));
    const updateIsOpen = (isOpenDataEvaluated) => {
        dispatch(updateTileState(datasetId, { isOpenDataEvaluated }));
    };
    const isOpenTransformations = useSelector((state) => getIsOpenTransformations(state, datasetId));
    const isReady = dataset?.number_of_rows !== -1;
    const isProcessing = dataset?.status_name !== "DONE";
    const isError = dataset?.dataset_status === "ERROR";
    const isWideDatasetWarning = dataset?.wideDatasetWarning;
    const dateFieldOptions = dataset?.ranked_fields !== undefined
        ? dataset.ranked_fields
            .filter((f) => f.type === "date")
            .map((f) => ({
            value: f,
            label: f.label
        }))
        : [];
    const isDisabled = !(isEnabled("dataset-whats-changed") || isEnabled("test-train-split")) ||
        isError ||
        isProcessing ||
        dataset?.number_of_rows === undefined ||
        dataset?.number_of_rows === -1 ||
        dateFieldOptions.length === 0;
    return (React.createElement("div", { id: "totalDataEvaluatedSection_" + dataset?.id, className: (isOpen ? styles.openOverview : styles.overview) + " " + (isDisabled ? styles.isDisabled : ""), style: {
            width: isOpen || isOpenTransformations || !isEnabled("sense-transformations") ? "100%" : "calc(66.666% - 16px)",
            maxHeight: isOpen ? "750px" : "150px",
            overflow: isOpen ? "auto" : "hidden",
            zIndex: 5
        }, onClick: isOpen !== true && !isDisabled ? () => updateIsOpen(!isOpen) : undefined },
        React.createElement("div", { className: styles.senseTitle, style: { alignItems: "center", lineHeight: "16px" } },
            React.createElement("div", { className: styles.sTitle },
                isError ? (React.createElement("span", { className: styles.sIcon },
                    React.createElement(FontAwesomeIcon, { icon: faExclamationTriangle, style: { color: "#F8CD7E" } }))) : isReady && isProcessing !== true ? (React.createElement("span", { className: styles.sIcon },
                    React.createElement("img", { src: RadarIcon, style: { height: "16px", width: "16px" } }))) : isProcessing === true ? (React.createElement(LoadingIndicator, { style: {
                        margin: "0px",
                        marginTop: "-5px",
                        marginRight: "5px",
                        height: "0px",
                        width: "50px",
                        transform: "scale(0.5)"
                    } })) : (React.createElement("span", { className: styles.sIcon },
                    React.createElement(FontAwesomeIcon, { icon: faCircle, style: { color: "rgb(200, 202, 212)" } }))),
                React.createElement("span", { style: { width: "calc(100% - 50px)", color: "#868595" } }, isError ? "Data Evaluation Incomplete" : isProcessing ? "Evaluating Data..." : "Total Data Evaluated")),
            isOpen ? (React.createElement("div", { id: "totalDataEvaluatedSectionClose_" + dataset?.id, className: styles.closeButton, onClick: () => updateIsOpen(!isOpen) }, isDisabled ? (React.createElement("div", null)) : (React.createElement(Button, { style: { padding: "3px" } },
                React.createElement("div", { className: styles.selIcon, title: "Close" },
                    React.createElement(FontAwesomeIcon, { icon: faCompressArrowsAlt })))))) : (React.createElement("div", { className: styles.expandIcon }, isDisabled ? (React.createElement("div", null)) : (React.createElement("div", { className: styles.selIcon },
                React.createElement(FontAwesomeIcon, { icon: faExpandArrowsAlt })))))),
        React.createElement("div", { className: styles.overviewGroup },
            React.createElement("div", { className: styles.overviewItem },
                React.createElement("div", { className: styles.overviewMedium }, dataset?.number_of_rows !== undefined &&
                    dataset?.number_of_rows !== -1 &&
                    (dataset?.number_of_rows > 0 || dataset.options.target_model_params?.data?.summary_detail != "low")
                    ? numeral(dataset?.number_of_rows).format("0.0a")
                    : "..."),
                React.createElement("div", { className: styles.overviewTop }, "ROWS")),
            React.createElement("div", { className: styles.overviewItem },
                React.createElement("div", { className: styles.overviewMedium }, dataset?.fields && Object.keys(dataset.fields).length > 0
                    ? isWideDatasetWarning
                        ? dataset.labels?.length
                        : Object.keys(dataset.fields).length
                    : "..."),
                React.createElement("div", { className: styles.overviewTop }, "COLUMNS"))),
        isOpen && React.createElement(DataCollection, { dataset: dataset })));
};
export default DataEvaluatedPanel;
